import { COUNTER_DURATION } from '@/constants/animations'
import Image from 'next/image'
import React, { useEffect, useState } from 'react'
import { View } from 'reshaped'
import styles from './AnimatedNumber.module.css'

export interface AnimatedNumberProps {
  number: string | number
  totalClaimableLength: number
  showTokenIcon?: boolean
}

export function AnimatedNumber({
  number,
  totalClaimableLength,
  showTokenIcon = true,
}: AnimatedNumberProps) {
  const [isMobile, setIsMobile] = useState(false)
  const [fontSize, setFontSize] = useState('180px')
  const displayValue = number

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const handleResize = () => {
        const mobile = window.innerWidth < 768
        setIsMobile(mobile)
        calculateFontSize(totalClaimableLength, mobile)
      }

      handleResize()
      window.addEventListener('resize', handleResize)
      return () => window.removeEventListener('resize', handleResize)
    }
  }, [totalClaimableLength])

  useEffect(() => {
    calculateFontSize(totalClaimableLength, isMobile)
  }, [totalClaimableLength, isMobile])

  const calculateFontSize = (length: number, mobile: boolean) => {
    // Font size mapping for different number lengths
    const mobileFontSizes: Record<number, number> = {
      1: 100,
      2: 100,
      3: 100,
      4: 100,
      5: 80,
      6: 72,
      7: 64,
      8: 58,
      9: 54,
    }

    const desktopFontSizes: Record<number, number> = {
      1: 167,
      2: 167,
      3: 167,
      4: 167,
      5: 167,
      6: 160,
      7: 130,
      8: 120,
      9: 110,
    }

    const fontSizes = mobile ? mobileFontSizes : desktopFontSizes
    const calculatedSize = fontSizes[length] || (mobile ? 50 : 147)

    setFontSize(`${calculatedSize}px`)
  }

  return (
    <View
      height="100%"
      width="100%"
      direction="row"
      justify="center"
      align="center"
      attributes={{
        style: {
          fontFamily: 'Arial, sans-serif',
          transformOrigin: 'center center',
        },
      }}
    >
      <View
        width="100%"
        className={styles.numberBase}
        data-text={displayValue}
        attributes={{ style: { fontSize } }}
      >
        <View className={`${styles.numberElem} ${styles.numberElemOuter}`}>
          {displayValue}
        </View>
        <div
          className={`${styles.numberElem} ${styles.numberElemInner}`}
          data-text={displayValue}
        >
          <>
            {displayValue}

            {showTokenIcon && (
              <View
                className={styles.tokenIconContainer}
                attributes={{ style: { animationDelay: `${COUNTER_DURATION}s` } }}
              >
                <Image
                  src="/assets/token-icon.png"
                  alt="ZORA Token"
                  className={styles.tokenIcon}
                  width={64}
                  height={64}
                  priority
                />
              </View>
            )}
          </>
        </div>
      </View>
    </View>
  )
}
